import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import constants from "../../constants/tutorialAutomaton";
import { useUser } from "../../context/userContext";
import { useGrammar } from "context/grammarContext";
import { useAlert } from "../../context/Feedback/alertContext";
import styles from "../../assets/jss/material-dashboard-react/components/dialogHistorical";
import "./classes.css";
import HistoricalSection from "./HistoricalSection";
import DataService from "../../services/DataService";
import historicDialogConstants from "../../constants/Dialogs/historicalDialogConstants";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";

const useStyles = makeStyles(styles);

/**
 * Componente React que exibe um diálogo histórico de gramáticas criadas pelo usuário.
 *
 * @component
 *
 * @returns {JSX.Element} O JSX para renderizar o diálogo histórico de gramáticas.
 *
 * @example
 * <HistoricalGrammarDialog />
 */
export default function HistoricalGrammarDialog() {
  const { language, user, setUser, credential } = useUser();
  const { setAlert } = useAlert();
  const {
    isActiveDialogHistoricalGrammar,
    setActiveDialogHistoricalGrammar,
    setOriginalGrammar,
    setGrammarName,
    grammarCreatedDate,
    setGrammarCreatedDate,
  } = useGrammar();

  const [historicalToday, setHistoricalToday] = useState([]);
  const [historicalYesterday, setHistoricalYesterday] = useState([]);
  const [historicalLastWeeks, setHistoricalLastWeeks] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const classes = useStyles();

  const handleClickMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  /**
   * Função para exclusão em massa por período (Gramática)
   * @param {number} months Quantidade de meses (1, 6, 12)
   */
  const handleBulkDelete = async (months) => {
    handleCloseMenu();

    const confirmMessage =
      historicDialogConstants.clear.bulkDelete.title[language];

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await DataService.deleteGrammarOlderThan(
        user.email,
        months,
        credential
      );

      if (response && response.data) {
        setUser({ ...user, historicalGrammar: response.data });

        if (grammarCreatedDate) {
          const exists = response.data.some(
            (h) => h.createdDate === grammarCreatedDate
          );
          if (!exists) {
            setOriginalGrammar("");
            setGrammarName("");
            setGrammarCreatedDate(null);
          }
        }

        setAlert({
          active: true,
          text: historicDialogConstants.clear.bulkDelete.alert.success[
            language
          ],
          color: "success",
          severity: "success",
        });
      }
    } catch (error) {
      setAlert({
        active: true,
        text: historicDialogConstants.clear.bulkDelete.alert.error[language],
        color: "danger",
        severity: "error",
      });
    }
  };

  /**
   * Verifica se a data do elemento é hoje.
   *
   * @param {Object} element Elemento do histórico.
   * @param {Date} today Data atual.
   * @returns {boolean} Verdadeiro se a data for hoje.
   */
  const isToday = (element, today) =>
    today.getDate() === new Date(element.lastUpdatedDate).getDate() &&
    today.getMonth() === new Date(element.lastUpdatedDate).getMonth() &&
    today.getFullYear() === new Date(element.lastUpdatedDate).getFullYear();

  /**
   * Verifica se a data do elemento é ontem.
   *
   * @param {Object} element Elemento do histórico.
   * @param {Date} today Data atual.
   * @returns {boolean} Verdadeiro se a data for ontem.
   */
  const isYesterday = (element, today) =>
    today.getDate() - 1 === new Date(element.lastUpdatedDate).getDate() &&
    today.getMonth() === new Date(element.lastUpdatedDate).getMonth() &&
    today.getFullYear() === new Date(element.lastUpdatedDate).getFullYear();

  /**
   * Ordena uma lista de gramáticas históricas com base na data de criação.
   */
  const sortHistorical = (historical, isAscending) => {
    return historical?.slice().sort((a, b) => {
      const dateA = new Date(a.lastUpdatedDate);
      const dateB = new Date(b.lastUpdatedDate);

      return isAscending ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Atualiza os períodos históricos de gramáticas com base na data de criação.
   */
  const updateHistoricalPeriods = (sortedHistorical) => {
    const today = new Date();
    let historicalTempToday = [];
    let historicalTempYesterday = [];
    let historicalTempLastWeeks = [];

    sortedHistorical.forEach((hg) => {
      let grToHtml =
        typeof hg.variables === "string" && hg.variables.split("\n").length > 2
          ? `${hg.variables.split("\n")[0]}\n${
              hg.variables.split("\n")[1]
            }\n...`
          : hg.variables;

      const date = new Date(hg.lastUpdatedDate);

      const formattedDate = `${date.getDate()}/${
        date.getMonth() + 1
      }/${date.getFullYear()} - ${date.getHours()}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;

      const obj = {
        name: hg.name,
        value: grToHtml,
        original: hg.variables,
        date: formattedDate,
        createdDate: hg.createdDate,
      };

      if (isToday(hg, today)) {
        historicalTempToday.push(obj);
      } else if (isYesterday(hg, today)) {
        historicalTempYesterday.push(obj);
      } else {
        historicalTempLastWeeks.push(obj);
      }
    });

    setHistoricalToday(historicalTempToday);
    setHistoricalYesterday(historicalTempYesterday);
    setHistoricalLastWeeks(historicalTempLastWeeks);
  };

  /**
   * Fecha o diálogo histórico de gramáticas.
   */
  const handleClose = () => {
    setActiveDialogHistoricalGrammar(false);
  };

  /**
   * Seleciona uma gramática com base no período e no índice da lista.
   */
  const selectedGrammar = (index, list) => {
    let selectedItem = null;

    if (list === "today") {
      selectedItem = historicalToday[index];
    } else if (list === "yesterday") {
      selectedItem = historicalYesterday[index];
    } else {
      selectedItem = historicalLastWeeks[index];
    }

    if (selectedItem) {
      setOriginalGrammar(selectedItem.original);
      setGrammarCreatedDate(selectedItem.createdDate);
      setGrammarName(selectedItem.name);
      setActiveDialogHistoricalGrammar(false);
    }
  };

  const deleteHistoricalGrammar = async (index, time) => {
    let itemToDelete = null;

    if (time === "today") {
      itemToDelete = historicalToday[index];
    } else if (time === "yesterday") {
      itemToDelete = historicalYesterday[index];
    } else if (time === "lastWeeks") {
      itemToDelete = historicalLastWeeks[index];
    }

    if (!itemToDelete || !itemToDelete.createdDate) {
      return;
    }

    try {
      const response = await DataService.deleteHistoricalGr(
        user.email,
        itemToDelete.createdDate,
        credential
      );

      if (response && response.data) {
        setUser({ ...user, historicalGrammar: response.data });

        if (grammarCreatedDate === itemToDelete.createdDate) {
          setOriginalGrammar("");
          setGrammarName("");
          setGrammarCreatedDate(null);
        }
      }
    } catch (error) {
      setAlert({
        active: true,
        text: historicDialogConstants.clear.alert.error[language],
        color: "danger",
        severity: "error",
      });
    }
  };

  /**
   * Hook de efeito usado para atualizar os períodos históricos ao detectar mudanças no histórico de gramáticas.
   */
  useEffect(() => {
    if (
      Array.isArray(user.historicalGrammar) &&
      user.historicalGrammar.length > 0
    ) {
      const sortedHistorical = sortHistorical(user.historicalGrammar, false);
      updateHistoricalPeriods(sortedHistorical);
    } else {
      setHistoricalToday([]);
      setHistoricalYesterday([]);
      setHistoricalLastWeeks([]);
    }
  }, [user.historicalGrammar]);

  return (
    <div>
      <Dialog
        open={isActiveDialogHistoricalGrammar}
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle
          className={classes.dialog}
          id="alert-dialog-slide-title"
          style={{ backgroundColor: "rgb(243, 242, 242)" }}
        >
          <div className="wrapper-title">
            {constants.historic[language]}

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconButton
                aria-controls="bulk-delete-menu-grammar"
                aria-haspopup="true"
                onClick={handleClickMenu}
                style={{ padding: 8 }}
                title={historicDialogConstants.clear.title[language]}
              >
                <DeleteSweepIcon fontSize="large" />
              </IconButton>

              {/* Menu Suspenso */}
              <Menu
                id="bulk-delete-menu-grammar"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
              >
                <MenuItem disabled style={{ opacity: 0.7, fontWeight: "bold" }}>
                  {historicDialogConstants.clear.inputLabel[language]}
                </MenuItem>
                <MenuItem onClick={() => handleBulkDelete(1)}>
                  <DeleteSweepIcon
                    style={{ marginRight: 10, color: "#f44336" }}
                  />{" "}
                  1 {historicDialogConstants.clear.period.month[language]}
                </MenuItem>
                <MenuItem onClick={() => handleBulkDelete(6)}>
                  <DeleteSweepIcon
                    style={{ marginRight: 10, color: "#f44336" }}
                  />{" "}
                  6 {historicDialogConstants.clear.period.months[language]}
                </MenuItem>
                <MenuItem onClick={() => handleBulkDelete(12)}>
                  <DeleteSweepIcon
                    style={{ marginRight: 10, color: "#f44336" }}
                  />{" "}
                  1 {historicDialogConstants.clear.period.year[language]}
                </MenuItem>
              </Menu>

              <div className="wrapper-button-close button-margin">
                <Button
                  onClick={handleClose}
                  style={{
                    backgroundColor: "#999",
                    color: "#FFF",
                    marginLeft: 8,
                  }}
                >
                  X
                </Button>
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent
          className={classes.dialog}
          style={{
            backgroundColor: "rgb(243, 242, 242)",
          }}
        >
          {historicalToday.length > 0 && (
            <HistoricalSection
              title={constants.today[language]}
              data={historicalToday}
              onButtonClick={(index) => selectedGrammar(index, "today")}
              onDeleteClick={(index) => deleteHistoricalGrammar(index, "today")}
            />
          )}

          {historicalYesterday.length > 0 && (
            <HistoricalSection
              title={constants.yesterday[language]}
              data={historicalYesterday}
              onButtonClick={(index) => selectedGrammar(index, "yesterday")}
              onDeleteClick={(index) =>
                deleteHistoricalGrammar(index, "yesterday")
              }
            />
          )}

          {historicalLastWeeks.length > 0 && (
            <HistoricalSection
              title={constants.lastWeeks[language]}
              data={historicalLastWeeks}
              onButtonClick={(index) => selectedGrammar(index, "lastWeeks")}
              onDeleteClick={(index) =>
                deleteHistoricalGrammar(index, "lastWeeks")
              }
            />
          )}
        </DialogContent>
        <DialogActions
          style={{ backgroundColor: "rgb(243, 242, 242)" }}
          className={classes.dialog}
        ></DialogActions>
      </Dialog>
    </div>
  );
}
